import React, { useEffect, useState } from "react";
import { ArrowRight, Star, Check, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { GOOGLE_REVIEWS_URL } from "../mock";

const FloatBadge = ({ children, className = "" }) => (
  <div
    className={`flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-[0.72rem] font-medium shadow-[0_10px_30px_-12px_rgba(0,0,0,0.35)] backdrop-blur ${className}`}
  >
    {children}
  </div>
);

const GreenCheck = () => (
  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[var(--cm-green)]">
    <Check size={11} className="text-white" strokeWidth={3} />
  </span>
);

export default function Hero({ car }) {
  const [stickerShown, setStickerShown] = useState(false);
  useEffect(() => {
    const inT = setTimeout(() => setStickerShown(true), 900);
    const outT = setTimeout(() => setStickerShown(false), 5200);
    return () => { clearTimeout(inT); clearTimeout(outT); };
  }, []);
  return (
    <section id="top" className="relative pt-[110px] lg:pt-[130px]">
      <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left */}
          <div>
            <div className="mb-7 flex items-center gap-2">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: "var(--cm-green)" }}
              />
              <span className="label-mono">
                Auto usate selezionate e verificate
              </span>
            </div>

            <h1 className="h-display text-[clamp(2.4rem,5.4vw,4.2rem)]">
              L'auto usata che puoi{" "}
              <span className="font-serif-cm text-[#5b5b54]">conoscere prima ancora di vederla.</span>
            </h1>

            <p className="mt-7 max-w-[470px] text-[1.02rem] leading-relaxed text-[#5b5b54]">
              Foto, video, manutenzione, stato d'uso e difetti dichiarati prima
              della visita.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/auto"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--cm-ink)] px-6 py-3.5 text-[0.92rem] font-medium text-white transition-transform duration-200 hover:scale-[1.03]"
              >
                Scopri le auto
              </Link>
              <Link
                to="/#vendi"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--cm-line)] px-6 py-3.5 text-[0.92rem] font-medium transition-all duration-200 hover:border-[var(--cm-ink)]"
              >
                Vendi con Carmaps
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[0.9rem] font-medium transition-opacity hover:opacity-70"
              >
                <Star size={16} className="fill-[var(--cm-green)] text-[var(--cm-green)]" />
                <span className="font-semibold">4,8</span> su Google · +50 recensioni
              </a>
              <span className="text-[0.9rem] text-[#7a7a72]">Auto selezionate e verificate</span>
              <span className="text-[0.9rem] text-[#7a7a72]">Foto reali per ogni auto</span>
            </div>
          </div>

          {/* Right card */}
          {car && (
            <Link to={`/auto/${car.handle}`} className="group relative block text-left">
              <div className="relative overflow-hidden rounded-[22px] border border-[var(--cm-line)] bg-white shadow-[0_40px_90px_-40px_rgba(0,0,0,0.4)]">
                <div className="relative aspect-[16/11] overflow-hidden">
                  <img
                    src={car.image}
                    alt={car.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute left-4 top-4">
                    <span className="rounded-md bg-black/55 px-2.5 py-1 font-mono-cm text-[0.62rem] uppercase tracking-[0.16em] text-white backdrop-blur">
                      Ultima selezione
                    </span>
                  </div>
                  <div
                    data-testid="hero-sticker"
                    className={`absolute right-4 top-4 flex -rotate-3 items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[0.72rem] font-semibold text-[var(--cm-ink)] shadow-[0_12px_30px_-12px_rgba(0,0,0,0.55)] ring-1 ring-black/5 transition-all duration-500 ${stickerShown ? "opacity-100 scale-100" : "pointer-events-none scale-90 opacity-0"}`}
                  >
                    <Flame size={13} className="text-[#e8543b]" /> La più richiesta
                  </div>
                  {car.km && (
                    <FloatBadge className="absolute left-4 top-[38%]">
                      <GreenCheck /> Chilometraggio {car.km}
                    </FloatBadge>
                  )}
                  {car.verified && (
                    <FloatBadge className="absolute right-4 top-[54%]">
                      <GreenCheck /> Stato d'uso verificato
                    </FloatBadge>
                  )}
                  <FloatBadge className="absolute bottom-4 left-4">
                    <GreenCheck /> {car.photos} foto reali
                  </FloatBadge>
                </div>

                <div className="flex items-center justify-between gap-4 px-6 py-5">
                  <div>
                    <p className="text-[1.05rem] font-semibold">
                      {car.brand}{" "}
                      <span className="font-normal text-[#6f6f68]">{car.trim}</span>
                    </p>
                    <p className="mt-1 font-mono-cm text-[0.66rem] uppercase tracking-[0.16em] text-[#9a9a92]">
                      Rif. {car.ref}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[1.15rem] font-semibold">{car.price}</span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--cm-ink)] text-white transition-transform duration-300 group-hover:translate-x-1">
                      <ArrowRight size={18} />
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
